import axios from '../helpers/axios'
import qs from 'querystring';
import { logError } from '../services';
import {
  INeighborhoodOverview,
  IPunchlist,
  IResponse,
  IPunchlistPost,
  IPunchlistTerritory,
  INeighborhoodPunchlist,
  INeighborhoodPunchlistUser,
  IPunchlistSearchResult,
  IPunchlistDashboard,
  IPunchlistDetail,
  IPunchlistTodoItemDetail,
  IPunchlistTodo,
  IUpdatePunchlistTodo,
  IPunchlistItemsReport,
  IPunchlistNeighborhoodPerformance,
  IPunchlistVendorPerformance,
  IJobWithoutPunchlist,
  IDropdownResponse,
  IPunchlistUpdateTodoItem,
  ISelectableVendor,
  IPunchlistAssignToLookup,
  IPunchlistAssignToLookupSlim
} from '../models';

export const getPunchlistDashboard = async (filters?: {
  page?: number;
  perPage?: number | string;
  sortBy?: string;
  sortDirection?: string;
  neighborhoodId?: string | number;
  lotNumber?: string | number;
  vendorId?: string | number;
  startDate?: string;
  endDate?: string;
  searchText?: string;
  showArchived?: boolean;
  itemType?: string | number;
  assignedTo?: string;
}): Promise<IResponse<IPunchlistDashboard[]>> => {
  try {
    const { data } = await axios.get(`/api/punch-lists/dashboard`, {
      params: filters
    });
    return data;
  } catch (error: any) {
    logError(error, 'getPunchlistsRecentActivity failed');
    return Promise.reject(error.response.data);
  }
};

export const getPunchlistDashboardCsv = async (filters?: {
  neighborhoodId?: string | number;
  lotNumber?: string | number;
  vendorId?: string | number;
  startDate?: string;
  endDate?: string;
  searchText?: string;
  showArchived?: boolean;
  itemType?: string | number;
  assignedTo?: string;
}): Promise<string> => {
  try {
    const { data } = await axios.get(`/api/punch-lists/dashboard/csv`, {
      params: filters,
      responseType: 'blob'
    });
    console.table(filters)
    return data;
  } catch (error: any) {
    logError(error, 'getPunchlistDashboardCsv failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodPunchlist = async (
  neighborhoodId: number | string
): Promise<INeighborhoodOverview> => {
  try {
    const { data } = await axios.get(
      `/api/punch-lists/neighborhoods/${neighborhoodId}/recent-activity`
    );
    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodPunchlist failed');
    return Promise.reject(error);
  }
};

export const getNeighborhoodPunchlists = async (filters?: {
  page?: number;
  perPage?: number | string;
  sortBy?: string;
  sortDirection?: string;
  search?: string;
  territoryId?: string | number;
}): Promise<IResponse<INeighborhoodPunchlist[]>> => {
  try {
    const { data } = await axios.get(`/api/punch-lists/neighborhoods`, {
      params: filters
    });
    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodPunchlists failed');
  }
};

export const getPunchlists = async (filters?: {
  page?: number;
  perPage?: number | string;
  sortBy?: string;
  sortDirection?: string;
  neighborhoodId?: string | number;
  jobNumber?: string | number;
}): Promise<IResponse<IPunchlist[]>> => {
  try {
    const { data } = await axios.get(`/api/punch-lists`, {
      params: filters
    });
    return data;
  } catch (error: any) {
    logError(error, 'getPunchlists failed');
    return error.response.data;
  }
};

export const getPunchlistItem = async (
  punchlistItemId: number | string
): Promise<IPunchlistTodoItemDetail> => {
  try {
    const { data } = await axios.get(`/api/punch-lists/items/${punchlistItemId}`);
    return data;
  } catch (error: any) {
    logError(error, 'getPunchlist failed');
    return Promise.reject(error);
  }
};

export const deletePunchlistItem = async (punchlistItemId: number | string) => {
  try {
    const { data } = await axios.delete(`/api/punch-lists/items/${punchlistItemId}`);
    return data;
  } catch (error: any) {
    logError(error, 'deletePunchlistItem failed');
    return Promise.reject(error);
  }
};

export const postPunchlistItemComment = async (
  punchlistItemId: number | string,
  comment: string
): Promise<string | number> => {
  try {
    const { data } = await axios.post(`/api/punch-lists/items/${punchlistItemId}/comments`, {
      comment
    });
    return data;
  } catch (error: any) {
    logError(error, 'postPunchlistItemComment failed');
    return Promise.reject(error);
  }
};

export const createPunchlistItemFiles = async (
  punchlistCommentsId: number | string,
  imagePost: FormData
): Promise<string[]> => {
  try {
    const { data } = await axios.post(
      `/api/punch-lists/comments/${punchlistCommentsId}/files`,
      imagePost
    );
    return data;
  } catch (error: any) {
    logError(error, 'createPunchlistItemFiles failed');
    return Promise.reject(error);
  }
};

export const createPunchlist = async (post: IPunchlistPost): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/punch-lists`, post);
    return data;
  } catch (error) {
    logError({ error, payload: post }, 'createPunchlist failed');
    return Promise.reject(error.response.data);
  }
};

export const getPunchlistTerritories = async (): Promise<IPunchlistTerritory[]> => {
  try {
    const { data } = await axios.get(`/api/punch-lists/territories`);
    return data;
  } catch (error) {
    logError(error, 'getPunchlist failed');
    return Promise.reject(error);
  }
};

export const deletePunchlist = async (id: number): Promise<any> => {
  try {
    const { data } = await axios.delete(`/api/punch-lists/${id}`);
    return data;
  } catch (error) {
    logError(error, 'deletePunchlist failed');
    return Promise.reject(error);
  }
};

export const updatePunchlist = async (
  id: string | number,
  put: {
    name: string;
    description: string;
    isArchived: boolean;
  }
): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/punch-lists/${id}`, put);
    return data;
  } catch (error) {
    logError({ error, payload: { id, put } }, 'updatePunchlist failed');
    return Promise.reject(error.response.data);
  }
};

export const addPunchlistItem = async (
  id: string | number,
  put: IPunchlistUpdateTodoItem
): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/punch-lists/${id}/items`, put);
    return data;
  } catch (error) {
    logError({ error, payload: { id, put } }, 'updatePunchlistItems failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodPunchlistUsers = async (
  id: string
): Promise<INeighborhoodPunchlistUser[]> => {
  try {
    const { data } = await axios.get(`/api/punch-lists/neighborhoods/${id}/recent-users`);
    return data;
  } catch (error) {
    logError(error, 'getPunchlist failed');
    return Promise.reject(error);
  }
};

export const getPunchlistDetails = async (
  id: string | number,
  filters?: {
    vendorSageId?: number | string;
  }
): Promise<IPunchlistDetail> => {
  try {
    const { data } = await axios.get(`/api/punch-lists/${id}`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getPunchlistTodos failed');
    return Promise.reject(error);
  }
};

export const getPunchlistSearchResults = async (filters?: {
  page?: number;
  perPage?: number | string;
  sortBy?: string;
  sortDirection?: string;
  search?: string;
}): Promise<IResponse<IPunchlistSearchResult[]>> => {
  try {
    const { data } = await axios.get(`/api/punch-lists/search`, {
      params: filters
    });
    return data;
  } catch (error: any) {
    logError(error, 'getPunchlistSearchResults failed');
  }
};

export const getPunchlistTodos = async (filters?: {
  page?: number;
  perPage?: number | string;
  sortBy?: string;
  sortDirection?: string;
  neighborhoodId?: string | number;
  lotNumber?: string | number;
  vendorId?: string | number;
  vendorSageId: string | number;
  assignedTo?: string | number;
  includeArchived?: boolean;
  startDate?: string;
  endDate?: string;
  excludeEmptyForVendorView: boolean;
}): Promise<IResponse<IPunchlistTodo[]>> => {
  try {
    const { data } = await axios.get(`/api/punch-lists/to-dos`, {
      params: filters
    });
    return data;
  } catch (error: any) {
    logError(error, 'getPunchlistTodos failed');
    return Promise.reject(error.response.data);
  }
};

export const updatePunchlistTodo = async (
  todoItemId: number | string,
  put: IUpdatePunchlistTodo,
  vendorSageId?: string | number
): Promise<any> => {
  try {
    const { data } = await axios.put(
      `/api/punch-lists/to-dos/${todoItemId}${vendorSageId ? `?vendorSageId=${vendorSageId}` : ''}`,
      put
    );
    return data;
  } catch (error) {
    logError({ error, payload: { todoItemId, put } }, 'updatePunchlistTodo failed');
    return Promise.reject(error.response.data);
  }
};

export const updatePunchlistTodoSort = async (
  punchlistId: number | string,
  put: {
    punchlistItemId: number;
    sortOrder: number;
  }[],
  vendorSageId?: string | number
): Promise<any> => {
  try {
    const { data } = await axios.put(
      `/api/punch-lists/${punchlistId}/to-dos/sort${
        vendorSageId ? `?vendorSageId=${vendorSageId}` : ''
      }`,
      {
        punchlistItems: put
      }
    );
    return data;
  } catch (error) {
    logError({ error, payload: { punchlistId, put } }, 'updatePunchlistTodoSort failed');
    return Promise.reject(error.response.data);
  }
};

export const getPunchlistItemsReport = async (filters?: {
  page?: number;
  perPage?: number | string;
  sortBy?: string;
  sortDirection?: string;
  neighborhoodId?: string | number;
  lotNumber?: string | number;
  territoryId?: string | number;
  description?: string;
  isPastDue?: boolean;
  vendorId?: string | number;
  startDate?: string;
  endDate?: string;
}): Promise<IResponse<IPunchlistItemsReport[]>> => {
  try {
    const { data } = await axios.get(`/api/punch-lists/reports/items`, {
      params: filters
    });
    return data;
  } catch (error: any) {
    logError(error, 'getPunchlistItemsReport failed');
    return Promise.reject(error.response.data);
  }
};

export const getPunchlistNeighborhoodPerformanceReport = async (filters?: {
  sortBy?: string;
  sortDirection?: string;
  neighborhoodId?: string | number;
  territoryId?: string | number;
  itemCount?: string | number;
  vendorId?: string | number;
}): Promise<IPunchlistNeighborhoodPerformance[]> => {
  try {
    const { data } = await axios.get(`/api/punch-lists/reports/neighborhood-performance`, {
      params: filters
    });
    return data;
  } catch (error: any) {
    logError(error, 'getPunchlistNeighborhoodPerformanceReport failed');
    return Promise.reject(error.response.data);
  }
};

export const getPunchlistVendorPerformanceReport = async (filters?: {
  sortBy?: string;
  sortDirection?: string;
  neighborhoodId?: string | number;
  territoryId?: string | number;
  itemCount?: string | number;
  vendorId?: string | number;
}): Promise<IPunchlistVendorPerformance[]> => {
  try {
    const { data } = await axios.get(`/api/punch-lists/reports/vendor-performance`, {
      params: filters
    });
    return data;
  } catch (error: any) {
    logError(error, 'getPunchlistVendorPerformanceReport failed');
    return Promise.reject(error.response.data);
  }
};

export const mergePunchlistPurchaseOrderChecklist = async (
  purchseOrderChecklistId: number | string
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `/api/punch-lists/merge-purchase-order-checklist/${purchseOrderChecklistId}`
    );
    return data;
  } catch (error) {
    logError({ error }, 'mergePunchlistPurchaseOrderChecklist failed');
    return Promise.reject(error.response.data);
  }
};

export const mergePunchlistFieldWalkthrough = async (
  jobId: number | string,
  payload: {
    assignments: {
      fieldWalkthroughItemId: number;
      assignedTo: string | null;
      partnerId: number | null;
    }[];
  }
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `/api/punch-lists/merge-field-walk-through/${jobId}`,
      payload
    );
    return data;
  } catch (error) {
    logError({ error }, 'mergePunchlistPurchaseOrderChecklist failed');
    return Promise.reject(error.response.data);
  }
};

export const downloadPunchlistTodos = async (filters?: {
  neighborhoodId?: string | number;
  vendorId?: string | number;
  assignedTo?: string | number;
  startDate?: string;
  endDate?: string;
}): Promise<string> => {
  try {
    const { data } = await axios.post(`/api/punch-lists/todos/download`, null, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError({ error }, 'downloadPunchlistTodos failed');
    return Promise.reject(error.response.data);
  }
};

export const createPunchlistImages = async (imagePost: FormData): Promise<string[]> => {
  try {
    const { data } = await axios.post(`/api/punch-lists/images`, imagePost);
    return data;
  } catch (error: any) {
    logError(error, 'createPunchlistImages failed');
    return Promise.reject(error);
  }
};

export const getJobsWithoutPunchlists = async (filters?: {
  neighborhoodId?: string | number;
}): Promise<IJobWithoutPunchlist[]> => {
  try {
    const { data } = await axios.get(`/api/jobs/${filters?.neighborhoodId}/without-punchlists`, {
      params: filters
    });
    return data;
  } catch (error: any) {
    logError(error, 'getJobsWithoutPunchlists failed');
    return Promise.reject(error.response.data);
  }
};

export const getPunchlistItemTypes = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/punch-lists/item-types`);
    return data;
  } catch (error: any) {
    logError(error, 'getPunchlistItemTypes failed');
    return Promise.reject(error.response.data);
  }
};

export const addTemplateToPunchlist = async (
  punchlistId: string | number,
  templateId: string | number
) => {
  try {
    const { data } = await axios.put(`/api/punch-lists/${punchlistId}/template/${templateId}`);
    return data;
  } catch (error: any) {
    logError(error, 'addTemplateToPunchlist failed');
    return Promise.reject(error.response.data);
  }
};

export const getPunchlistVendors = async (filters?: {
  page?: number;
  perPage?: number | string;
  sortBy?: string;
  sortDirection?: string;
  isActive?: boolean;
  name?: string;
}): Promise<IResponse<ISelectableVendor[]>> => {
  try {
    const { data } = await axios.get(`/api/punch-lists/vendors`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getPunchListVendors failed');
    return Promise.reject(error);
  }
};

/**
 *
 * @param {boolean} filters.includeGeneral - Flag indicating whether or not to include the fake user used for general assignment in the list
 * @returns
 */
export const getPunchlistAssignToLookup = async (filters: {
  sageJobId: number | string,
  territoryId: number | string,
  includeGeneralAssignment?: boolean,
}): Promise<IPunchlistAssignToLookup[]> => {
  try {
    const { data } = await axios.get(
      `/api/punch-lists/assign-to-lookup`, {
        params: filters
      });
    return data;
  } catch (error: any) {
    logError(error, 'getPunchlistAssignToLookup failed');
    return Promise.reject(error);
  }
};

// small version of getPunchlistAssignToLookup with optional filters //
export const getPunchlistAssignToLookupSlim = async (filters?: {
  sageJobId?: number | string,
  territoryId?: number | string,
  includeGeneralAssignment?: boolean
}): Promise<IPunchlistAssignToLookupSlim[]> => {
  try {
    const { data } = await axios.get(
      '/api/punch-lists/assign-to-lookup-slim',
      { params: filters }
    );
    return data;
  } catch (error: any) {
    console.error('getPunchlistAssignToLookup failed', error);
    return Promise.reject(error);
  }
};

export const getPunchlistTodoListPdf = async (filters?: {
  territoryId?: number | string;
  neighborhoodId?: number | string;
  lotNumber?: number | string;
  vendorId?: number | string;
  assignedTo?: string;
  startDate?: string;
  endDate?: string;
  vendorSageId?: number | string;
  includeArchived?: boolean;
  sageJobId?: number | string;
}): Promise<Blob> => {
  try {
    const response = await axios.get(`/api/punch-lists/pdf-todo-list`, {
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    logError(error, 'getPunchlistTodoListPdf failed');
    return Promise.reject(error);
  }
};